import { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Container,
  Typography,
  ListItem,
  ListItemText,
  Button,
  InputAdornment,
  TextField,
  Stack,
  Alert,
  Snackbar,
} from "@common-ui/ui";
import {
  Business,
  Email,
  Phone,
  Link,
  Numbers,
  TextSnippet,
} from "@mui/icons-material";

import EditCustomerDialog from "../dialogs/EditCustomerDialog";

import { updateOrgCustomerDetails } from "../../../services/orgCustomer";
import MandatoryIcon from "../../../components/MandatoryIcon";

const CustomerDetails = ({
  orgId,
  customerDetails,
  setIsSuccess,
  doesUserHavePermission,
}) => {
  const [toastOpen, setToastOpen] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  useEffect(() => {
    if (customerDetails) {
      setIsSuccess(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerDetails]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: customerDetails?.name?.trim(),
      account: customerDetails?.account?.trim(),
      email: customerDetails?.email?.trim(),
      phone: customerDetails?.phone?.trim(),
      website: customerDetails?.website?.trim(),
      notes: customerDetails?.notes?.trim(),
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().max(255).required("Name is required"),
      account: Yup.string().trim(),
      email: Yup.string().email().trim(),
      phone: Yup.number(),
      website: Yup.string().url().trim(),
      notes: Yup.string().trim(),
    }),
    onSubmit: async () => {
      let { name, account, email, phone, website, notes } = formik.values;
      let attributes = {
        name,
        account,
        email,
        phone,
        website,
        notes,
      };

      attributes = Object.entries(attributes).reduce(
        (acc, [key, _]) =>
          attributes[key] === customerDetails[key]
            ? typeof attributes[key] === "string" && attributes[key] === ""
              ? ((acc[key] = " "), acc)
              : acc
            : typeof attributes[key] === "string" && attributes[key] === ""
            ? ((acc[key] = " "), acc)
            : ((acc[key] = attributes[key]), acc),
        {}
      );

      try {
        const response = await updateOrgCustomerDetails(
          orgId,
          customerDetails.system_id,
          attributes
        );
        if (response) {
          setOpenSuccessDialog(true);
        }
      } catch (error) {
        alert(error);
      }
    },
  });

  const handleCancelClick = () => {
    formik.resetForm();
  };

  const handleSuccessModal = () => {
    setToastOpen(true);
    setOpenSuccessDialog(false);
    setIsSuccess(true);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToastOpen(false);
  };

  return (
    <>
      <Container maxWidth="md" component="main">
        <ListItem
          alignItems="flex-start"
          sx={{ width: "60%", maxWidth: "60%", pr: 0, pl: 0 }}
        >
          <ListItemText
            primary={
              <>
                <Typography color="textPrimary" variant="h3">
                  Details
                </Typography>
              </>
            }
          />
          <div></div>
        </ListItem>
        <form onSubmit={formik.handleSubmit}>
          <ListItem
            alignItems="flex-start"
            sx={{
              width: "60%",
              maxWidth: "60%",
              pr: 0,
              pl: 0,
              justifyContent: "flex-start",
            }}
          >
            <Typography sx={{ mt: 2, mb: 3 }} color="textPrimary" variant="h4">
              Client Information
            </Typography>
          </ListItem>
          <ListItem
            alignItems="flex-start"
            sx={{
              width: "60%",
              maxWidth: "60%",
              pr: 0,
              pl: 0,
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="subtitle1">
              <MandatoryIcon />
              Client Name
            </Typography>
            <TextField
              disabled={!doesUserHavePermission}
              id="name"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Business />
                  </InputAdornment>
                ),
              }}
              size="small"
              error={Boolean(formik.touched.name && formik.errors.name)}
              fullWidth
              helperText={formik.touched.name && formik.errors.name}
              placeholder="Company or Organization Name"
              margin="normal"
              sx={{ mt: 1 }}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </ListItem>
          <ListItem
            alignItems="flex-start"
            sx={{
              width: "60%",
              maxWidth: "60%",
              pr: 0,
              pl: 0,
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ mt: 1 }} variant="subtitle1">
              Account
            </Typography>
            <TextField
              disabled={!doesUserHavePermission}
              id="account"
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Numbers />
                  </InputAdornment>
                ),
              }}
              size="small"
              fullWidth
              helperText={formik.touched.account && formik.errors.account}
              placeholder="Account"
              margin="normal"
              sx={{ mt: 1 }}
              name="account"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.account}
            />
          </ListItem>
          <Typography sx={{ mt: 2, mb: 3 }} color="textPrimary" variant="h4">
            Contact Information
          </Typography>
          <ListItem
            alignItems="flex-start"
            sx={{
              width: "60%",
              maxWidth: "60%",
              pr: 0,
              pl: 0,
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ mt: 1 }} variant="subtitle1">
              Email
            </Typography>
            <TextField
              disabled={!doesUserHavePermission}
              id="email"
              sx={{ mt: 1 }}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Email />
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
              error={Boolean(formik.touched.email && formik.errors.email)}
              fullWidth
              helperText={formik.touched.email && formik.errors.email}
              placeholder="Email"
              margin="normal"
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="email"
              value={formik.values.email}
              variant="outlined"
            />
          </ListItem>
          <ListItem
            alignItems="flex-start"
            sx={{
              width: "60%",
              maxWidth: "60%",
              pr: 0,
              pl: 0,
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ mt: 1 }} variant="subtitle1">
              Phone
            </Typography>
            <TextField
              disabled={!doesUserHavePermission}
              id="phone"
              sx={{ mt: 1 }}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Phone />
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
              error={Boolean(formik.touched.phone && formik.errors.phone)}
              fullWidth
              helperText={formik.touched.phone && formik.errors.phone}
              placeholder="Phone"
              margin="normal"
              name="phone"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="text"
              value={formik.values.phone}
              variant="outlined"
            />
          </ListItem>
          <ListItem
            alignItems="flex-start"
            sx={{
              width: "60%",
              maxWidth: "60%",
              pr: 0,
              pl: 0,
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ mt: 1 }} variant="subtitle1">
              Website
            </Typography>
            <TextField
              disabled={!doesUserHavePermission}
              id="website"
              sx={{ mt: 1 }}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Link />
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
              error={Boolean(formik.touched.website && formik.errors.website)}
              fullWidth
              helperText={formik.touched.website && formik.errors.website}
              placeholder="Website"
              margin="normal"
              name="website"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="website"
              value={formik.values.website}
              variant="outlined"
            />
          </ListItem>
          <ListItem
            alignItems="flex-start"
            sx={{
              width: "60%",
              maxWidth: "60%",
              pr: 0,
              pl: 0,
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography sx={{ mt: 1 }} variant="subtitle1">
              Notes
            </Typography>
            <TextField
              disabled={!doesUserHavePermission}
              id="notes"
              multiline
              rows={5}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <TextSnippet />
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
              error={Boolean(formik.touched.notes && formik.errors.notes)}
              fullWidth
              helperText={formik.touched.notes && formik.errors.notes}
              placeholder="Type your notes here"
              margin="normal"
              name="notes"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              type="text"
              value={formik.values.notes}
              variant="outlined"
            />
          </ListItem>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="flex-end"
            spacing={{ xs: 1, md: 2 }}
            sx={{ pt: 0, width: "60%", maxWidth: "60%" }}
          >
            <div>
              <Button
                color="primary"
                fullWidth
                size="large"
                onClick={handleCancelClick}
                variant="text"
              >
                Cancel
              </Button>
            </div>
            {doesUserHavePermission ? (
              <div>
                <Button
                  color="primary"
                  disabled={
                    formik.isSubmitting || !(formik.isValid && formik.dirty)
                  }
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Save
                </Button>
              </div>
            ) : (
              ""
            )}
          </Stack>
        </form>
        <EditCustomerDialog
          open={openSuccessDialog}
          handleClose={handleSuccessModal}
        />
      </Container>
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleCloseToast}
      >
        <Alert
          onClose={handleCloseToast}
          severity="success"
          sx={{ width: "100%" }}
          variant="filled"
        >
          Client details updated successfully.
        </Alert>
      </Snackbar>
    </>
  );
};

export default CustomerDetails;
